import { useEffect, useRef, useState } from 'react';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { ReportPageOption } from '@/core/models/reports.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { selectedClient } from '@/core/selectedClient';
import { reportsService } from '@/core/services/reports';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { SOLE_TRADER_CLIENT_TYPE, useClientType } from '@/core/useClientType';
import { Icon } from '@/kit/Icon';
import { Select } from '@/kit/Select';
import { Toggle } from '@/kit/Toggle';
import { DialogClose, DialogContent, type DialogComponentProps } from '@/kit/dialog';
import './TaxreturnReportsettingsOption.scss';

export interface ReportOptionsDialogData {
  clientId: string;
  /** #176: 0 Annual (default), 1 Management. */
  reportType?: number;
}

/**
 * Accounts report options for a client (Angular TaxreturnReportsettingsOptionComponent): cover/draft flags, column
 * header style and the pages of each report variant. Closes with true after a successful save.
 */
const SOLE_TRADER_HIDDEN_PAGES =
  /^(director'?s?[’']? report|statement of comprehensive income|statement of changes in equity|statement of cash flows?)$/i;

export function TaxreturnReportsettingsOption({
  data,
  dialogRef,
}: DialogComponentProps<ReportOptionsDialogData, boolean>) {
  // BUG-045: unticked until the saved setting loads, as the old application's default (the API defaults it to false).
  const [showReportsDate, setShowReportsDate] = useState(false);
  const [showDraftMessage, setShowDraftMessage] = useState(false);
  const [showAccountantOnCoverPage, setShowAccountantOnCoverPage] = useState(false);
  const [hideStatutoryDeclarations, setHideStatutoryDeclarations] = useState(false);
  const [columnHeaderStyle, setColumnHeaderStyle] = useState('YYYY');
  const [columnHeaderStyles, setColumnHeaderStyles] = useState<string[]>([]);
  const [dataSource, setDataSource] = useState<ReportPageOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  // Save stays off until the saved options have loaded: saving the defaults above would overwrite every flag and page.
  const [loaded, setLoaded] = useState(false);
  const saving = useRef(false);
  const mounted = useRef(true);

  /** #176: the list the dialog was opened from - Management Accounts options are kept apart from Annual ones. */
  const reportType = data?.reportType === 1 ? 1 : 0;
  const [clientId] = useState<string | null>(() => data?.clientId ?? selectedClient()?.id ?? null);
  // A sole trader has one set of accounts, so only the Full Report page selection applies.
  const isSoleTrader = useClientType(clientId) === SOLE_TRADER_CLIENT_TYPE;

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (!clientId) {
      notifyError('Select a client from Accounts first.');
      dialogRef.close();
      return;
    }
    let cancelled = false;
    reportsService.getReportOptions(clientId, reportType).then(
      (options) => {
        if (cancelled) return;
        setShowReportsDate(options.showReportsDate);
        setShowDraftMessage(options.showDraftMessage);
        setShowAccountantOnCoverPage(options.showAccountantOnCoverPage);
        setHideStatutoryDeclarations(options.hideStatutoryDeclarations);
        setColumnHeaderStyle(options.columnHeaderStyle);
        setColumnHeaderStyles(options.columnHeaderStyles);
        setDataSource(options.pages.map((p) => ({ ...p })));
        setLoaded(true);
      },
      (error) => {
        if (!cancelled) notifyError(error);
      },
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const save = async () => {
    if (!clientId) {
      notifyError('Open a client from Accounts first.');
      return;
    }
    if (saving.current || !loaded) {
      return;
    }
    saving.current = true;
    setIsSaving(true);
    try {
      await reportsService.saveReportOptions({
        clientId,
        showReportsDate,
        showDraftMessage,
        showAccountantOnCoverPage,
        columnHeaderStyle,
        hideStatutoryDeclarations,
        // The hidden pages are saved switched off, so they cannot appear in a sole trader's accounts.
        pages: dataSource.map((p) =>
          isSoleTrader && SOLE_TRADER_HIDDEN_PAGES.test(p.name)
            ? { pageId: p.pageId, full: false, abridged: false, filleted: false }
            : { pageId: p.pageId, full: p.full, abridged: p.abridged, filleted: p.filleted },
        ),
        reportType,
      });
      notifySuccess(NotificationTextMessage.successMessage);
      dialogRef.close(true);
    } catch (error) {
      notifyError(error);
    } finally {
      saving.current = false;
      if (mounted.current) {
        setIsSaving(false);
      }
    }
  };

  // Pages that do not form part of a sole trader's accounts.
  const rows = isSoleTrader ? dataSource.filter((p) => !SOLE_TRADER_HIDDEN_PAGES.test(p.name)) : dataSource;

  const setPage = (pageId: number, field: 'full' | 'abridged' | 'filleted', value: boolean) =>
    setDataSource((rows) => rows.map((row) => (row.pageId === pageId ? { ...row, [field]: value } : row)));

  const allowedFlag = (row: ReportPageOption, field: 'full' | 'abridged' | 'filleted') =>
    field === 'full' ? row.allowedFull : field === 'abridged' ? row.allowedAbridged : row.allowedFilleted;

  const variantColumn = (
    id: string,
    header: string,
    field: 'full' | 'abridged' | 'filleted',
  ): DataTableColumn<ReportPageOption> => ({
    id,
    header,
    headerClassName: 'text-align-center',
    cellClassName: 'text-align-center',
    cell: (element) =>
      // BUG-051: only the pages valid for this report type get a checkbox.
      allowedFlag(element, field) ? (
        <Checkbox checked={element[field]} onChange={(event) => setPage(element.pageId, field, event.target.checked)} />
      ) : null,
  });

  // APT341: Full and Filleted only - there is no Abridged column any more. The saved abridged ticks are kept as they are (the save above sends
  // them back unchanged), so a report that already is Abridged still prints the pages it was saved with.
  const columns: DataTableColumn<ReportPageOption>[] = [
    { id: 'pageselection', header: 'Page Selection', cell: (element) => element.name },
    variantColumn('fullreport', 'Full Report', 'full'),
    ...(isSoleTrader ? [] : [variantColumn('filletedreport', 'Filleted Report', 'filleted')]),
  ];

  return (
    <div className="mat-dialog-wrapper">
      <DialogContent>
        <form onSubmit={(event) => event.preventDefault()}>
          <div className="main-contain">
            <div className="sidenav-header">
              <div className="d-flex align-items-center justify-space-between">
                <h2 className="m-0">
                  <span>Accounts Report Options</span>
                </h2>
                <DialogClose>
                  <Icon name="close" className="close" />
                </DialogClose>
              </div>
            </div>
          </div>
          <div className="sidenav-body">
            <div className="form-field">
              {/* 2 Nov review: the three toggles sat shoulder to shoulder; more room between them, wrapping if the dialog is narrow. */}
              <div className="report-options-toggles">
                <Toggle checked={showReportsDate} onCheckedChange={setShowReportsDate}>
                  Show Reports Date
                </Toggle>
                <Toggle checked={showDraftMessage} onCheckedChange={setShowDraftMessage}>
                  Show Draft Message
                </Toggle>
                <Toggle checked={showAccountantOnCoverPage} onCheckedChange={setShowAccountantOnCoverPage}>
                  Show Accountant on Cover Page
                </Toggle>
              </div>
            </div>
            <div className="form-field">
              <p className="fw-bold">Column Header Style</p>
              <Select
                className="w-250 mb-1"
                name="columnHeaderStyle"
                items={columnHeaderStyles}
                clearable={false}
                value={columnHeaderStyle}
                onChange={(value) => setColumnHeaderStyle(value as string)}
              />
              <Toggle checked={hideStatutoryDeclarations} onCheckedChange={setHideStatutoryDeclarations}>
                Hide Statutory Declarations
              </Toggle>
            </div>

            <div className="content-box hr-top">
              {/* item 137: the Accounts Builder starts from the Annual selection only, so the Management options
                  say nothing. */}
              {reportType === 0 && (
                <p className="font-size-12 text-gray mb-0 mt-5">
                  The Accounts Builder starts each period&rsquo;s pages from these selections, unless a rule fixes them.
                  A period whose pages were already saved in the Accounts Builder keeps its own choices.
                </p>
              )}
              <div className="sidenav-table sidenav-table-input wp-100 pt-1">
                <div className="mat-table-wrapper contact-detail-table">
                  <DataTable stickyHeader columns={columns} rows={rows} rowKey={(row) => row.pageId} />
                </div>
              </div>
            </div>
          </div>

          <div className="d-flex gap-15 hr-top pt-1 mt-1">
            <Button className="action-button primary-button" disabled={isSaving || !loaded} onClick={() => void save()}>
              Save
            </Button>
            <DialogClose as="button" type="button" className="action-button secondary-button">
              Cancel
            </DialogClose>
          </div>
        </form>
      </DialogContent>
    </div>
  );
}
